import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { AdminPartner } from '@/types';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { Loader2, Plus } from 'lucide-react';

const MODEL_LABEL: Record<string, string> = {
  none: 'None',
  per_account: 'Per paying client',
  percent_recurring: 'Percent of recurring',
};

export function AdminPartnersPage() {
  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api
      .getPartners()
      .then(({ partners }) => setPartners(partners))
      .catch((err) => console.error('Failed to load partners:', err))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Partners</h1>
            <p className="text-gray-600 mt-1">Affiliates and resellers, with their Salesforce link and commission status</p>
          </div>
          <Link to="/admin/users">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add partner user
            </Button>
          </Link>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All partners</CardTitle>
            <CardDescription>Status and counts come from Salesforce. Commission terms and statements live in the portal.</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-abby-primary" />
              </div>
            ) : partners.length === 0 ? (
              <p className="text-gray-600 py-8 text-center">No partners yet. Create a partner user under Users.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-3 font-medium">Partner</th>
                      <th className="pb-3 font-medium">Type</th>
                      <th className="pb-3 font-medium">Code</th>
                      <th className="pb-3 font-medium">Salesforce</th>
                      <th className="pb-3 font-medium text-right">Leads</th>
                      <th className="pb-3 font-medium text-right">Accounts</th>
                      <th className="pb-3 font-medium text-right">Paying</th>
                      <th className="pb-3 font-medium">Commission</th>
                      <th className="pb-3 font-medium text-right">Unpaid</th>
                      <th className="pb-3 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {partners.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="py-3">
                          <p className="font-medium text-gray-900">{p.company_name || 'Unnamed partner'}</p>
                          <p className="text-xs text-gray-500">{p.user_count} user{p.user_count === 1 ? '' : 's'}</p>
                        </td>
                        <td className="py-3">
                          <Badge variant={p.partner_type === 'affiliate' ? 'submitted' : 'secondary'}>
                            {p.partner_type === 'affiliate' ? 'Affiliate' : 'Reseller'}
                          </Badge>
                        </td>
                        <td className="py-3 font-mono text-xs">{p.referral_code || '-'}</td>
                        <td className="py-3">
                          {p.status === 'disabled' ? (
                            <Badge variant="destructive">Disabled</Badge>
                          ) : p.partner_type === 'affiliate' ? (
                            <Badge variant={p.partnership_status === 'Current' ? 'active' : 'pending'}>
                              {p.partnership_status || 'Not synced'}
                            </Badge>
                          ) : (
                            <Badge variant="active">Active</Badge>
                          )}
                        </td>
                        <td className="py-3 text-right tabular-nums">{p.partner_type === 'affiliate' ? p.lead_count : '-'}</td>
                        <td className="py-3 text-right tabular-nums">{p.account_count}</td>
                        <td className="py-3 text-right tabular-nums">{p.partner_type === 'affiliate' ? p.paying_count : '-'}</td>
                        <td className="py-3 text-gray-700">
                          {p.partner_type === 'affiliate'
                            ? p.commission_model
                              ? MODEL_LABEL[p.commission_model]
                              : <span className="text-amber-700">No terms set</span>
                            : 'Wholesale'}
                        </td>
                        <td className="py-3 text-right tabular-nums">
                          {p.partner_type === 'affiliate' ? formatCurrency(p.unpaid_commission) : '-'}
                        </td>
                        <td className="py-3 text-right">
                          <Link to={`/admin/partners/${p.id}`}>
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
